import {
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    effect,
    Injector,
    inject,
    input,
    numberAttribute,
    output,
    untracked,
} from '@angular/core';
import { areExpansionPanelStatesEqual, EXPANSION_PANEL_INITIAL_STATE } from './cost-to-build-client-ui-expansion-panel.state';
import { ExpansionPanelStore } from './cost-to-build-client-ui-expansion-panel.store';
import type { ExpansionPanelState, ExpansionPanelTriggeredEvent } from './cost-to-build-client-ui-expansion-panel.types';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [],
    providers: [ExpansionPanelStore],
    selector: 'ctb-expansion-panel',
    styleUrl: './cost-to-build-client-ui-expansion-panel.scss',
    templateUrl: './cost-to-build-client-ui-expansion-panel.html',
})
export class CtbExpansionPanelComponent {
    readonly #injector = inject(Injector);
    readonly #store = inject(ExpansionPanelStore);

    readonly isOpen = input(EXPANSION_PANEL_INITIAL_STATE.isOpen, { transform: booleanAttribute });
    readonly number = input(EXPANSION_PANEL_INITIAL_STATE.number, { transform: numberAttribute });
    readonly title = input<string>(EXPANSION_PANEL_INITIAL_STATE.title);

    readonly toggled = output<ExpansionPanelTriggeredEvent>();
    readonly stateChange = output<ExpansionPanelState>();

    readonly state = this.#store.state;

    #lastEmittedState: ExpansionPanelState | null = null;

    readonly syncStoreFromInputs = effect(() => this.#syncStoreFromInputs(), { injector: this.#injector });

    readonly emitStateChanges = effect(
        () => {
            const nextState = this.state();
            const previousState = this.#lastEmittedState;

            if (previousState !== null && areExpansionPanelStatesEqual(previousState, nextState)) {
                return;
            }

            this.#lastEmittedState = nextState;
            this.stateChange.emit(nextState);
        },
        { injector: this.#injector }
    );

    onToggle() {
        this.#store.toggle();
        this.toggled.emit({ isOpen: this.state().isOpen });
    }

    #syncStoreFromInputs() {
        const patch = {
            isOpen: this.isOpen(),
            number: this.number(),
            title: this.title(),
        };

        untracked(() => this.#store.setState(patch));
    }
}
